import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { checkinLoan } from '../../api/loans';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { AssetLoan } from '../../types/api';

export function CheckinLoanModal({
  loan,
  open,
  onClose,
}: {
  loan: AssetLoan;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const itemName = loan.asset.orderLine?.customerCatalogItem?.name ?? 'this item';

  const mutation = useMutation({
    mutationFn: () => checkinLoan(loan.id, notes.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loan', loan.id] });
      queryClient.invalidateQueries({ queryKey: ['asset', loan.assetId] });
      showToast(`"${itemName}" checked back in.`);
      setNotes('');
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not check in this loan.')),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Check in equipment"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button isLoading={mutation.isPending} onClick={() => mutation.mutate()}>
            Confirm check-in
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <p className="text-sm text-slate-600">
          Mark <span className="font-medium text-slate-900">{itemName}</span> as returned. It'll go back to spare
          stock, ready for the next request.
        </p>
        <Input
          label="Notes (optional)"
          placeholder="e.g. condition on return"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </Modal>
  );
}
